// src/store/catalog/thunks/fetchSixCatalogThunk.ts

import { FILE_TRANSFER_TOPIC, PUBSUB_TOPICS } from "@/config/networkConstants";
import { mapSixesResponse } from "@/store/mappers/mapSixesResponse";
import { initializeP2PStack } from "@/store/slices/poc/connectionSlice";
import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import { facilitateFileKeys } from "@/store/thunks/fileKey/facilitateFileKeysThunk";
import type { TurnConfig } from "@/types/poc/libp2p";

export const fetchSixCatalog = createAppAsyncThunk(
  "catalog/fetchSixes",
  async(search: string | undefined, { extra, dispatch, getState }) =>{
    const log = extra.loggerService.createLogger({ namespace: 'six3ro:six:catalog' });
    const apiResponse = await extra.sixCatalogService.fetchSixes(search);

    const mappedResponse = mapSixesResponse(apiResponse);
    const uniqueById = Array.from(
      new Map(mappedResponse.map(item => [item.id, item])).values()
    );

    // Collect all bootstrap nodes from the catalog for cross-six discovery
    const allBootstrapNodes: string[] = [];
    for (const six of uniqueById) {
      allBootstrapNodes.push(...six.bootstrapNodes);
    }

    // Deduplicate and filter empty bootstrap nodes
    const uniqueBootstrapNodes = [...new Set(allBootstrapNodes)].filter(node => node !== '');
    log.info('Collected bootstrap nodes from catalog:', uniqueBootstrapNodes);

    // Get coturn credentials from Redux state for TURN server authentication
    const state = getState();
    const coturnCredentials = state.organization.coturnCredentials;
    let turnConfig: TurnConfig | undefined = undefined;

    if (coturnCredentials?.urls !== undefined) {
      turnConfig = {
        username: coturnCredentials.username,
        credential: coturnCredentials.password,
        urls: coturnCredentials.urls
      };
      log.info('Using coturn credentials for P2P initialization', {
        expiresAt: coturnCredentials.username,
        urls: coturnCredentials.urls
      });
    } else {
      log.warn('No coturn credentials available, P2P WebRTC connections may fail');
    }

    uniqueById.forEach(six => {
      // Skip if already connected (idempotency check)
      const existingConnection = state.p2pConnection.connections[six.id];
      if (existingConnection?.status === 'connected') {
        log.debug(`P2P already connected for six ${six.id}, skipping initialization`);
        return;
      }

      log.info('initializing p2p stack for six', six.id, six.bootstrapNodes, six.swarmKey);
      const fileListTopic = `${PUBSUB_TOPICS.FILE_LIST}/${six.id}`;
      void (dispatch(initializeP2PStack({
          sixId: six.id,
          autoConnectLibp2p: true,
          autoInitializeHelia: true,
          autoInitializeMessaging: true,
          bootstrapNodes: uniqueBootstrapNodes,
          swarmKey: six.swarmKey,
          fileListTopic,
          fileTransferTopic: `${six.id}-${FILE_TRANSFER_TOPIC}`,
          turnConfig
        })));
      });

    // Fire-and-forget: facilitate missing file keys for recovered users.
    // The API call inside gives P2P stacks time to initialize.
    void dispatch(facilitateFileKeys());

    return uniqueById;
  }
);
